import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/model'
import { useRealtimeEnabled } from '@/features/realtime/lib'
import { fetchStateConfig } from '../api'
import { stateKeys } from './state.queryKeys'
import { selectDisplayStateByStatus, selectSidebarOrderPanels, selectStateDictionary } from './state.utils'

/**
 * Query options for fetching state configuration
 */
export function stateConfigOptions() {
  return queryOptions({
    queryKey: stateKeys.stateConfig(),
    queryFn: fetchStateConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    retry: 3,
  })
}

/**
 * Hook to fetch state configuration conditionally based on authentication
 * @returns The state configuration with hash, or undefined if not authenticated
 */
export function useStateConfigQuery() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  return useQuery({
    ...stateConfigOptions(),
    // Enable if authenticated OR if we have a token (since route is protected)
    enabled: isAuthenticated(),
  })
}

/**
 * Hook to poll for state config changes on the backend
 * and invalidate the cache when changes are detected
 *
 * @param pollInterval - Polling interval in milliseconds (default: 60000 - 1 minute)
 */
export function useStateConfigPoller(pollInterval = 60000) {
  const queryClient = useQueryClient()
  const { data: currentData } = useStateConfigQuery()
  const isRealtimeEnabled = useRealtimeEnabled()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  // Background polling for state config changes
  useQuery({
    queryKey: stateKeys.stateConfigPoller(),
    queryFn: async () => {
      try {
        const response = await fetchStateConfig()
        const newStateNames = response.states.map(s => s.name).sort().join('|')
        const currentStateNames = currentData?.states.map(s => s.name).sort().join('|')

        // Check for changes in realtime updates feature flag
        const newRealtimeUpdates = response.features?.realtimeUpdates
        const currentRealtimeUpdates = currentData?.features?.realtimeUpdates

        // Check for changes in sidebar panel configuration
        const hashPanels = (panels: typeof response.orderPanels) =>
          (panels ?? []).map(p => `${p.id}:${[...p.states].sort().join(',')}`).sort().join('|')
        const newOrderPanelsHash = hashPanels(response.orderPanels)
        const currentOrderPanelsHash = hashPanels(currentData?.orderPanels)

        // If state names, realtime feature flag, or panel config have changed, invalidate the main config query
        const stateNamesChanged = currentStateNames && newStateNames !== currentStateNames
        const realtimeUpdatesChanged = currentRealtimeUpdates !== undefined
          && newRealtimeUpdates !== currentRealtimeUpdates
        const orderPanelsChanged = currentData !== undefined && newOrderPanelsHash !== currentOrderPanelsHash

        if (stateNamesChanged || realtimeUpdatesChanged || orderPanelsChanged) {
          queryClient.invalidateQueries({ queryKey: stateKeys.stateConfig() })
        }

        return { stateNames: newStateNames, realtimeUpdates: newRealtimeUpdates, orderPanelsHash: newOrderPanelsHash }
      } catch (error) {
        // Log error but don't throw to prevent UI disruption
        console.error('Failed to check state config changes:', error)
        return null
      }
    },
    // Only enable polling when user is authenticated
    enabled: isAuthenticated() && !isRealtimeEnabled,
    refetchInterval: pollInterval,
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider hash check stale to ensure regular polling
    retry: 3,
    // Silent failures - we don't want to disrupt the UI
    throwOnError: false,
  })
}

/**
 * Hook to get a dictionary of state definitions conditionally based on authentication
 * @returns A record of state definitions keyed by state name, or undefined if not authenticated
 */
export function useStateDictionaryQuery() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  return useQuery({
    ...stateConfigOptions(),
    enabled: isAuthenticated(),
    select: (data) => {
      return selectStateDictionary(data.states)
    },
  })
}

/**
 * Selector hook to get error states from the state configuration
 * @returns An array error states keyed by state name, or undefined if not authenticated
 */
export function useStateConfigErrorStates() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  return useQuery({
    ...stateConfigOptions(),
    enabled: isAuthenticated(),
    select: (data) => {
      return data.states.filter(state => state.isError).map(state => state.name)
    },
  })
}

/**
 * Hook to get the ordered list of sidebar panels derived from state configuration.
 * @returns The sidebar panels, or undefined if not authenticated.
 */
export function useSidebarOrderPanels() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  return useQuery({
    ...stateConfigOptions(),
    enabled: isAuthenticated(),
    select: selectSidebarOrderPanels,
  })
}

/**
 * Hook to get a map of order statuses to their display states conditionally based on authentication
 * @returns A record where keys are status names and values are display state objects, or undefined if not authenticated
 */
export function useDisplayStateByStatus() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)

  return useQuery({
    ...stateConfigOptions(),
    enabled: isAuthenticated(),
    select: (data) => {
      return selectDisplayStateByStatus(data.states)
    },
  })
}
